<template>
  <BaseHeader
    :title="$t('valuation.valuation-form.title')"
    :style-variant="5"
  >
    <template v-slot:design>
      <HeaderDesign />
    </template>

    <template v-slot:desc>
      <p>
        {{ $t('valuation.valuation-form.body-texts.0') }}
      </p>
      <p><b>{{ $t('valuation.valuation-form.body-texts.3') }}</b></p>
    </template>
    <template v-slot:content>
      <v-slide-y-transition>
        <v-form
          ref="form"
          @submit.prevent="handleSubmit"
        >
          <v-card-text>
            <v-row
              class="justify-center mt-1 d-flex"
              :class="{'mb-n6':!inputs.type, 'justify-space-between mb-3 mb-md-0':inputs.type}"
            >
              <v-col
                cols="12"
                md="6"
                class="py-1 py-md-2"
              >
                <AddressField
                  v-model="inputs.place"
                  :country-code="countryCode"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col
                v-if="inputs.place"
                cols="12"
                sm="6"
                md="3"
                class="py-1 py-md-2"
              >
                <v-select
                  v-model="inputs.type"
                  :label="$t('valuation.valuation-form.input-type-label')"
                  :items="typeList"
                  :rules="[rules.required]"
                  outlined
                  dense
                  required
                />
              </v-col>

              <v-col
                v-if="inputs.type"
                cols="12"
                sm="6"
                md="3"
                class="py-1 py-md-2"
              >
                <v-select
                  v-if="subTypeList"
                  v-model="inputs.subType"
                  :label="$t(subTypeLabel)"
                  :items="subTypeList"
                  :rules="[rules.required]"
                  outlined
                  dense
                  required
                />
              </v-col>
            </v-row>

            <v-expand-transition>
              <div v-if="expanded">
                <v-row class="mb-n3 mb-md-n5">
                  <v-col
                    cols="12"
                    md="3"
                    class="py-1 py-md-2"
                  >
                    <v-text-field
                      v-model.number="inputs.buildingYear"
                      type="number"
                      :label="$t('valuation.labels.building-year')"
                      :hint="getRangeHint('buildingYear')"
                      :min="getRangeMin('buildingYear')"
                      :max="getRangeMax('buildingYear')"
                      :rules="rules.buildingYear"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                    class="py-1 py-md-2"
                  >
                    <v-text-field
                      v-if="inputs.type === types.MULTI_FAMILY_HOUSE"
                      v-model.number="inputs.numberOfUnits"
                      type="number"
                      :label="$t('valuation.valuation-form.sub-type-labels.number-units')"
                      step="1"
                      :hint="getRangeHint('numberOfUnits')"
                      :min="getRangeMin('numberOfUnits')"
                      :max="getRangeMax('numberOfUnits')"
                      :rules="rules.numberOfUnits"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />

                    <v-text-field
                      v-else
                      v-model.number="inputs.numberOfRooms"
                      type="number"
                      :label="$t('valuation.valuation-form.sub-type-labels.number-rooms')"
                      step="0.5"
                      :hint="getRangeHint('numberOfRooms')"
                      :min="getRangeMin('numberOfRooms')"
                      :max="getRangeMax('numberOfRooms')"
                      :rules="rules.numberOfRooms"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                    class="py-1 py-md-2"
                  >
                    <v-text-field
                      v-model.number="inputs.livingArea"
                      type="number"
                      suffix="m²"
                      :label="$t('valuation.labels.living-area')"
                      :hint="getRangeHint('livingArea')"
                      :min="getRangeMin('livingArea')"
                      :max="getRangeMax('livingArea')"
                      :rules="rules.livingArea"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                    class="py-1 py-md-2"
                  >
                    <v-text-field
                      v-if="inputs.type === types.APARTMENT"
                      v-model.number="inputs.balconyArea"
                      type="number"
                      :label="$t('valuation.labels.balcony-area')"
                      suffix="m²"
                      :hint="getRangeHint('balconyArea')"
                      :min="getRangeMin('balconyArea')"
                      :max="getRangeMax('balconyArea')"
                      :rules="rules.balconyArea"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />

                    <v-text-field
                      v-else
                      v-model.number="inputs.landArea"
                      type="number"
                      :label="$t('valuation.labels.land-area')"
                      suffix="m²"
                      :hint="getRangeHint('landArea')"
                      :min="getRangeMin('landArea')"
                      :max="getRangeMax('landArea')"
                      :rules="rules.landArea"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    md="3"
                    class="py-1 py-md-2"
                  >
                    <v-select
                      v-model="inputs.condition"
                      :label="$t('valuation.labels.condition')"
                      :items="conditionList"
                      :rules="[rules.required]"
                      outlined
                      dense
                      required
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                    class="py-1 py-md-2"
                  >
                    <v-select
                      v-model="inputs.quality"
                      :label="$t('valuation.labels.quality')"
                      :items="qualityList"
                      :rules="[rules.required]"
                      outlined
                      dense
                      required
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                    class="py-1 py-md-2"
                  >
                    <v-text-field
                      v-if="inputs.type === types.MULTI_FAMILY_HOUSE"
                      v-model.number="inputs.annualRentIncome"
                      type="number"
                      label="Mieteinnahmen/Jahr"
                      :hint="getRangeHint('annualRentIncome')"
                      :min="getRangeMin('annualRentIncome')"
                      :max="getRangeMax('annualRentIncome')"
                      :rules="rules.annualRentIncome"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />

                    <v-text-field
                      v-if="inputs.type === types.APARTMENT && inputs.subType !== 'APARTMENT_TERRACED'"
                      v-model.number="inputs.floorNumber"
                      type="number"
                      label="Stockwerk"
                      :hint="getRangeHint('floorNumber')"
                      :min="getRangeMin('floorNumber')"
                      :max="getRangeMax('floorNumber')"
                      :rules="rules.floorNumber"
                      validate-on-blur
                      outlined
                      dense
                      required
                    />
                  </v-col>
                </v-row>

                <v-expand-transition>
                  <v-row v-if="isState('ERROR')">
                    <v-col>
                      <v-alert
                        type="error"
                        dense
                        tile
                        text
                      >
                        {{ errorMessage }}
                      </v-alert>
                    </v-col>
                  </v-row>
                </v-expand-transition>

                <!-- reverse (if !mobile) so that the submit button is first focused on tab -->
                <v-card-actions class="flex-column flex-md-row-reverse">
                  <LoadingButton
                    color="primary"
                    type="submit"
                    class="my-8 my-md-0"
                    :loading="isState('SUBMITTING')"
                  >
                    {{ $t('buttons.valuation.cta') }}
                  </LoadingButton>
                  <v-spacer />
                  <v-btn
                    text
                    @click="resetForm"
                  >
                    {{ $t('buttons.valuation.delete') }}
                  </v-btn>
                </v-card-actions>
              </div>
            </v-expand-transition>
          </v-card-text>
        </v-form>
      </v-slide-y-transition>
    </template>
  </BaseHeader>
</template>

<script>
import * as rules from '@/lib/validation'
import LoadingButton from '@/components/loading-button'
import GET_INPUT_LIMITS from './queries/getInputLimits.gql'
import CREATE_VALUATION from './queries/createValuation.gql'
import AddressField from './AddressField'
import brandingMixin from '@/mixins/branding'
import featureNames from '@/lib/featureNames'
import BaseHeader from '@/components/BaseHeader.vue'
import HeaderDesign from './HeaderDesign.vue'

const states = {
  IDLE: 'IDLE',
  SUBMITTING: 'SUBMITTING',
  ERROR: 'ERROR'
}

const types = {
  APARTMENT: 'APARTMENT',
  HOUSE: 'HOUSE',
  MULTI_FAMILY_HOUSE: 'MULTI_FAMILY_HOUSE'
}

const typeListAll = [
  { value: types.APARTMENT, text: 'valuation.property-type.apartment-normal' },
  { value: types.HOUSE, text: 'valuation.property-type.house-single-family' },
  { value: types.MULTI_FAMILY_HOUSE, text: 'valuation.property-type.multi-family-house' }
]

const subTypeLists = countryCode => ({
  [types.APARTMENT]: [
    { value: 'APARTMENT_TERRACED', text: 'valuation.property-sub-type.apartment-terraced' },
    { value: 'APARTMENT_NORMAL', text: 'valuation.property-sub-type.apartment-normal' },
    { value: 'APARTMENT_STUDIO', text: 'valuation.property-sub-type.apartment-studio' },
    { value: 'APARTMENT_MAISONETTE', text: 'valuation.property-sub-type.apartment-maisonette' },
    { value: 'APARTMENT_PENTHOUSE', text: countryCode === 'ch' ? 'valuation.property-sub-type.apartment-attika' : 'valuation.property-sub-type.apartment-penthouse' },
    { value: 'APARTMENT_ATTIC', text: 'valuation.property-sub-type.apartment-attic' }
  ],
  [types.HOUSE]: [
    { value: 'HOUSE_DETACHED', text: 'valuation.property-sub-type.house-detached' },
    { value: 'HOUSE_SEMI_DETACHED', text: 'valuation.property-sub-type.house-semi-detached' },
    { value: 'HOUSE_ROW_CORNER', text: 'valuation.property-sub-type.house-row-corner' },
    { value: 'HOUSE_ROW_MIDDLE', text: 'valuation.property-sub-type.house-row-middle' },
    { value: 'HOUSE_FARM', text: 'valuation.property-sub-type.house-farm' }
  ]
})

const qualityList = [
  { value: 'SIMPLE', text: 'valuation.quality.simple' },
  { value: 'NORMAL', text: 'valuation.quality.normal' },
  { value: 'HIGH', text: 'valuation.quality.high' },
  { value: 'LUXURY', text: 'valuation.quality.luxury' }
]

const conditionList = [
  { value: 'RENOVATION_NEEDED', text: 'valuation.condition.renovation-needed' },
  { value: 'WELL_MAINTAINED', text: 'valuation.condition.well-maintained' },
  { value: 'NEW_OR_RECENTLY_RENOVATED', text: 'valuation.condition.new-or-recently-renovated' }
]

const subTypeLabels = {
  [types.APARTMENT]: 'valuation.valuation-form.sub-type-labels.apartment',
  [types.HOUSE]: 'valuation.valuation-form.sub-type-labels.house'
}

const getInitialInputs = () => ({
  place: null,
  type: null,
  subType: null,
  buildingYear: '',
  numberOfRooms: '',
  numberOfUnits: '',
  livingArea: '',
  landArea: '',
  balconyArea: '',
  condition: '',
  quality: '',
  floorNumber: '',
  annualRentIncome: ''
})

export default {
  components: {
    LoadingButton,
    AddressField,
    BaseHeader,
    HeaderDesign
  },

  mixins: [brandingMixin],

  data: () => ({
    state: states.IDLE,
    errorMessage: '',
    inputs: getInitialInputs(),
    types,
    image: '/img/banner-valuation.png'
  }),

  computed: {
    expanded () {
      return this.inputs.type && this.inputLimits
    },

    typeList () {
      return typeListAll.filter(({ value }) => {
        const type = this.$features.feature(featureNames.VALUATION).config.enabledInAppTypes[value]
        return type && type !== 'MANUAL'
      }).map(({ value, text }) => {
        return {
          value,
          text: this.$t(text)
        }
      })
    },

    subTypeList () {
      return subTypeLists(this.countryCode)[this.inputs.type]?.map(({ value, text }) => {
        return {
          value,
          text: this.$t(text)
        }
      })
    },

    conditionList () {
      return conditionList.map(({ value, text }) => {
        return {
          value,
          text: this.$t(text)
        }
      })
    },

    qualityList () {
      return qualityList.map(({ value, text }) => {
        return {
          value,
          text: this.$t(text)
        }
      })
    },

    countryCode () {
      return (
        {
          'de-AT': 'at',
          'de-CH': 'ch'
        }[this.$auth.user.locale] || 'de'
      )
    },

    subTypeLabel () {
      return subTypeLabels[this.inputs.type]
    },

    rules () {
      return {
        required: rules.required,
        buildingYear: [rules.required, rules.integer, this.getRangeRule('buildingYear')],
        numberOfUnits: [rules.required, rules.integer, this.getRangeRule('numberOfUnits')],
        numberOfRooms: [rules.required, rules.halfNumber, this.getRangeRule('numberOfRooms')],
        livingArea: [rules.required, rules.integer, this.getRangeRule('livingArea')],
        balconyArea: [rules.required, rules.integer, this.getRangeRule('balconyArea')],
        landArea: [rules.required, rules.integer, this.getRangeRule('landArea')],
        floorNumber: [rules.required, rules.integer, this.getRangeRule('floorNumber')],
        annualRentIncome: [rules.required, rules.integer, this.getRangeRule('annualRentIncome')]
      }
    }
  },

  apollo: {
    inputLimits: {
      query: GET_INPUT_LIMITS,
      variables () {
        return {
          input: {
            countryCode: this.countryCode,
            propertyType: this.inputs.type
          }
        }
      },
      skip () {
        return !this.inputs.type
      }
    }
  },

  watch: {
    'inputs.type' () {
      this.resetFormPartially()
    },

    'inputs.place' () {
      if (!this.inputs.place) {
        this.resetForm()
      }
    },

    state (state, previousState) {
      if (previousState === states.ERROR) {
        this.errorMessage = ''
      }
    }
  },

  methods: {

    isState (state) {
      return this.state === state
    },

    setState (state) {
      this.state = state
    },

    getRangeHint (property) {
      const limit = this.getLimit(property)
      return limit ? `${this.$t('valuation.valuation-form.between')} ${limit.min} – ${limit.max}` : ''
    },

    getRangeMin (property) {
      const limit = this.getLimit(property)
      return limit ? limit.min : ''
    },

    getRangeMax (property) {
      const limit = this.getLimit(property)
      return limit ? limit.max : ''
    },

    getLimit (property) {
      if (!this.inputLimits) {
        return null
      }

      return this.inputLimits[property] || null
    },

    getRangeRule (key) {
      if (!this.inputLimits || !this.inputLimits[key]) {
        return true
      }
      const { min, max } = this.inputLimits[key]

      return value => rules.betweenRule(value, min, max)
    },

    async handleSubmit () {
      const formIsValid = this.$refs.form.validate()
      if (!formIsValid) {
        return
      }

      this.setState(states.SUBMITTING)

      const { place, ...inputs } = this.inputs
      const inputData = {
        ...inputs,
        ...place,
        buildingYear: parseInt(inputs.buildingYear),
        numberOfRooms: parseFloat(inputs.numberOfRooms),
        numberOfUnits: parseInt(inputs.numberOfUnits),
        livingArea: parseInt(inputs.livingArea),
        landArea: parseInt(inputs.landArea),
        balconyArea: parseInt(inputs.balconyArea),
        floorNumber: parseInt(inputs.floorNumber) || 0, // In case of APARTMENT_TERRACED it's 0
        annualRentIncome: parseInt(inputs.annualRentIncome)
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: CREATE_VALUATION,
          variables: {
            input: inputData
          }
        })
        this.$emit('created', data.valuation)
        this.setState(states.IDLE)
      } catch (err) {
        this.setState(states.ERROR)
        this.errorMessage =
          this.$t('alerts.valuation.error')
      }
    },

    resetForm () {
      this.inputs = getInitialInputs()
      this.$refs.form.resetValidation()
      this.setState(states.IDLE)
      this.$emit('reset')
    },

    // Reset most fields on type change
    resetFormPartially () {
      this.inputs = {
        ...getInitialInputs(),
        place: this.inputs.place,
        type: this.inputs.type
      }
      this.$refs.form.resetValidation()
      this.setState(states.IDLE)
      this.$emit('reset')
    }
  }
}
</script>

<style scoped>
.theme--light.v-btn.v-btn--disabled.v-btn--has-bg{
  background-color: lightgray !important;
  opacity: 0.6 !important;
}
</style>
